import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import type { ComponentProps } from "react";

/**
 * Props van een icoon uit `@/icons`.
 *
 * Bewust identiek aan de vorm die lucide-react en @untitledui/icons hadden:
 * een SVG-component dat `className`, `data-icon` en `aria-hidden` doorgeeft.
 * Daardoor blijven bestaande call-sites (`<Search className="size-4" />` en
 * `iconLeading={Search}`) werken zonder aanpassing.
 */
export type IconProps = Omit<ComponentProps<typeof HugeiconsIcon>, "icon" | "altIcon" | "showAlt" | "strokeWidth"> & {
    /**
     * Untitled UI typeert `strokeWidth` als `string | number` (SVG-conventie),
     * Hugeicons versmalt naar `number`. We accepteren beide en normaliseren,
     * zodat onze iconen overal waar een SVG-component werd verwacht passen.
     */
    strokeWidth?: string | number;
};

export type IconComponent = (props: IconProps) => React.ReactElement;

/** Projectbrede defaults — één plek, conform de Codelabs UI/UX-standaard §7.2. */
export const ICON_DEFAULTS = {
    size: 20,
    strokeWidth: 1.5,
    color: "currentColor",
} as const;

/**
 * Maakt van Hugeicons-icoondata een React-component met de projectdefaults.
 *
 * Een gewone functiecomponent (geen forwardRef): Untitled UI's `isReactComponent`
 * herkent die als eerste tak, en we hebben geen ref op het icoon nodig.
 */
export function createIcon(icon: IconSvgElement, displayName: string): IconComponent {
    const Icon = ({ strokeWidth, ...props }: IconProps) => (
        <HugeiconsIcon
            icon={icon}
            {...ICON_DEFAULTS}
            {...props}
            strokeWidth={strokeWidth === undefined ? ICON_DEFAULTS.strokeWidth : Number(strokeWidth)}
        />
    );

    Icon.displayName = displayName;

    return Icon;
}
